import { Form, Formik } from "formik";
import { useState } from "react";
import { ICFormikDropDown, ICFormikTextIArea, ICFormikTextInput } from "../../core-component/formik";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
} from "../../core-component/ui";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { cn } from "../../lib";
import { hasAnyModification, prepareMessageFromParams } from "../../utils/AppFunctions";
import {
  CREDIT_TYPE_LABEL_KEY,
  getCreditTypeOptions,
  GRANT_NOTE_MAX_LENGTH,
} from "../../utils/PurchaseConstants";
import { formatCredits } from "../../utils/PurchaseFormat";
import {
  CREDIT_GRANT_INITIAL_VALUES,
  creditGrantValidationSchema,
  getGrantBlockMessage,
  handleSubmitCreditGrant,
  ICreditGrantForm,
} from "./CreditGrantFormController";
import { IFirmCreditBalance } from "./CreditGrantViewController";

interface ICreditGrantFormProps {
  firmCredit: IFirmCreditBalance;
  handleClose: (status: boolean) => void;
}

interface IGrantDetailRowProps {
  label: string;
  value: string;
}

const GrantDetailRow: React.FC<IGrantDetailRowProps> = ({ label, value }) => (
  <div className="flex justify-between gap-2">
    <span className="text-sm text-muted">{label}</span>
    <span className="font-semibold">{value}</span>
  </div>
);

const CreditGrantForm: React.FC<ICreditGrantFormProps> = ({ firmCredit, handleClose }) => {
  const { t } = UseTranslationHook();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);

  const checkFormModification = (values: ICreditGrantForm) => {
    if (hasAnyModification(values, CREDIT_GRANT_INITIAL_VALUES)) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={CREDIT_GRANT_INITIAL_VALUES}
        validationSchema={creditGrantValidationSchema(t)}
        onSubmit={(values) =>
          handleSubmitCreditGrant(firmCredit.firm_id, values, setSubmitLoading, handleClose)
        }
      >
        {(formikProps) => {
          const { handleSubmit, values } = formikProps;
          const cell = firmCredit.balances.find(
            (balance) => balance.credit_type === values.credit_type,
          );
          const blockMessage = getGrantBlockMessage(t, firmCredit.balances, values);

          return (
            <FormRightModalWrapper
              containerClassName="!w-1/3"
              onClose={() => checkFormModification(values)}
              title={t("grant_credits")}
            >
              <Form onSubmit={handleSubmit} className="flex flex-col h-full">
                <div className="flex-1 space-y-4">
                  <div className="bg-base rounded-md p-4 space-y-1">
                    <GrantDetailRow label={t("firm_name")} value={firmCredit.firm_name} />
                    {cell && (
                        <GrantDetailRow
                          label={prepareMessageFromParams(t("current_type_balance"), [
                            ["type", t(CREDIT_TYPE_LABEL_KEY[cell.credit_type])],
                          ])}
                          value={formatCredits(t, cell.balance)}
                        />
                      )}
                    {cell &&
                      !cell.is_unlimited && (
                        <GrantDetailRow
                          label={t("can_still_add")}
                          value={formatCredits(t, cell.max_purchasable)}
                        />
                      )}
                  </div>
                  <ICFormikDropDown
                    {...formikProps}
                    name="credit_type"
                    placeholder={t("credit_type")}
                    options={getCreditTypeOptions(t)}
                    required
                  />
                  <div>
                    <ICFormikTextInput
                      {...formikProps}
                      fieldName="credits"
                      placeholder={t("credits")}
                      type="number"
                      required
                    />
                    {blockMessage && <div className="text-sm text-danger mt-1">{blockMessage}</div>}
                  </div>
                  <ICFormikTextIArea
                    {...formikProps}
                    name="note"
                    label={t("reason")}
                    maxLength={GRANT_NOTE_MAX_LENGTH}
                    required
                  />
                </div>
                <ICButton
                  loading={submitLoading}
                  disabled={!!blockMessage || submitLoading}
                  className={cn("w-full", { "opacity-50 cursor-not-allowed": !!blockMessage })}
                >
                  {t("submit")}
                </ICButton>
              </Form>
            </FormRightModalWrapper>
          );
        }}
      </Formik>
      {showDiscardWarning && (
        <ICConfirmationModal
          title={t("discard_warning")}
          message={t("discard_unsaved_changes")}
          onClose={(value) => {
            setShowDiscardWarning(false);
            value && handleClose(false);
          }}
        />
      )}
    </>
  );
};

export default CreditGrantForm;
